import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Loader as LoaderIcon,
  Plus as PlusIcon,
  RefreshCw as RefreshCwIcon,
  SearchX as SearchXIcon,
} from "lucide-react";
import type { WhatsAppTemplateDto } from "@multi-chat/shared";
import { AppNotice } from "../../../components/common/AppNotice.js";
import { ProductState } from "../../../components/common/ProductState/index.js";
import { ChannelMark } from "../../../components/channel/ChannelMark.js";
import { AnimatePresence } from "../../../lib/motion.js";
import { Button } from "../../../components/ui/button/index.js";
import type { TemplatesLoadState } from "../use-whatsapp-templates.js";
import type { TemplateLanguageOption } from "../template-management/template-language-options.js";
import { hasActiveTemplateListFilters } from "../template-list-presentation.js";
import type { TemplateListViewFilters } from "../template-list-presentation.js";
import { TemplateListRow, TemplateListSkeleton } from "./TemplateListRow.js";

const headCellClass =
  "sticky top-0 z-10 border-e border-line bg-surface-muted px-4 py-2.5 text-center text-[0.75rem] font-semibold whitespace-nowrap text-ink-muted shadow-[inset_0_-1px_0_var(--color-line-strong)] last:border-e-0";
const centeredHeadClass = "text-center";

const spinClass = "animate-[spin_0.8s_linear_infinite] motion-reduce:animate-none";

export function TemplateListContent({
  loadState,
  items,
  visibleItems,
  filters,
  lastSyncedAt,
  loadError,
  syncing,
  syncError,
  canManage,
  showManageActions,
  showRefresh,
  languageOptions,
  onCreate,
  onEdit,
  onDuplicate,
  onDelete,
  onCancelDelete,
  onConfirmDelete,
  pendingDeleteId,
  deleting,
  deleteError,
  onRefresh,
  onReload,
  onDismissSyncError,
  organizationId,
  onAttachHeaderImage,
}: {
  loadState: TemplatesLoadState;
  items: readonly WhatsAppTemplateDto[];
  visibleItems: readonly WhatsAppTemplateDto[];
  filters: TemplateListViewFilters;
  lastSyncedAt: string | null;
  loadError: string | null;
  syncing: boolean;
  syncError: string | null;
  canManage: boolean;
  showManageActions: boolean;
  showRefresh: boolean;
  languageOptions: readonly TemplateLanguageOption[];
  onCreate: () => void;
  onEdit: (template: WhatsAppTemplateDto) => void;
  onDuplicate: (template: WhatsAppTemplateDto) => void;
  onDelete: (template: WhatsAppTemplateDto) => void;
  onCancelDelete: () => void;
  onConfirmDelete: () => void;
  pendingDeleteId: number | null;
  deleting: boolean;
  deleteError: string | null;
  onRefresh: () => void;
  onReload: () => void;
  onDismissSyncError: () => void;
  organizationId: number;
  onAttachHeaderImage?: (templateId: number, file: File) => Promise<void>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const [previewTemplateId, setPreviewTemplateId] = useState<number | null>(null);
  const neverSynced = loadState === "ready" && lastSyncedAt === null && items.length === 0;
  const syncedEmpty = loadState === "ready" && lastSyncedAt !== null && items.length === 0;
  const noMatches =
    loadState === "ready" &&
    items.length > 0 &&
    visibleItems.length === 0 &&
    hasActiveTemplateListFilters(filters);

  return (
    <div className="grid min-w-0 gap-3 p-4 sm:px-5 sm:pb-5">
      {syncError ? (
        <AppNotice
          severity="error"
          description={syncError}
          testId="whatsapp-templates-sync-error"
          onDismiss={onDismissSyncError}
          dismissLabel={t("whatsapp.dismiss")}
          action={
            showRefresh ? (
              <Button type="button" size="sm" variant="outline" onClick={onRefresh} disabled={syncing}>
                {t("whatsapp.templates.retrySync")}
              </Button>
            ) : null
          }
        />
      ) : null}

      {loadState === "loading" ? (
        <div role="status" aria-live="polite" aria-busy="true">
          <span className="sr-only">{t("whatsapp.templates.loading")}</span>
          <TemplateListSkeleton />
        </div>
      ) : null}

      {loadState === "error" ? (
        <AppNotice
          severity="error"
          description={loadError ?? t("whatsapp.unreachable")}
          testId="whatsapp-templates-load-error"
          action={
            <Button type="button" size="sm" variant="outline" onClick={onReload}>
              {t("whatsapp.retry")}
            </Button>
          }
        />
      ) : null}

      {neverSynced ? (
        <ProductState
          tone="empty"
          testId="whatsapp-templates-never-synced"
          visual={<ChannelMark platformType="WHATSAPP" size="md" />}
          title={t("whatsapp.templates.neverSynced.title")}
          description={t("whatsapp.templates.neverSynced.message")}
          action={
            <>
          {showRefresh ? (
            <Button type="button" variant="outline" size="sm" onClick={onRefresh} disabled={syncing}>
              {syncing ? (
                <LoaderIcon size={16} className={spinClass} aria-hidden="true" />
              ) : (
                <RefreshCwIcon size={16} aria-hidden="true" />
              )}
              {syncing ? t("whatsapp.templates.syncing") : t("whatsapp.templates.refresh")}
            </Button>
          ) : null}
          {!canManage ? (
            <p className="m-0 text-[0.8125rem] text-muted">{t("whatsapp.templates.readonly")}</p>
          ) : null}
            </>
          }
        />
      ) : null}

      {syncedEmpty ? (
        <ProductState
          tone="empty"
          testId="whatsapp-templates-empty"
          visual={<ChannelMark platformType="WHATSAPP" size="list" />}
          title={t("whatsapp.templates.empty.title")}
          description={t("whatsapp.templates.empty.message")}
          action={
            <>
          {showManageActions ? (
            <Button type="button" variant="default" size="sm" onClick={onCreate}>
              <PlusIcon size={16} aria-hidden="true" />
              {t("whatsapp.templates.manage.createAction")}
            </Button>
          ) : !canManage ? (
            <p className="m-0 text-[0.8125rem] text-muted">{t("whatsapp.templates.empty.noCreate")}</p>
          ) : null}
            </>
          }
        />
      ) : null}

      {noMatches ? (
        <ProductState
          tone="filtered"
          testId="whatsapp-templates-no-matches"
          visual={
            <span className="inline-grid size-9 place-items-center rounded-full bg-surface-muted text-ink-muted">
              <SearchXIcon size={16} aria-hidden="true" />
            </span>
          }
          title={t("whatsapp.templates.list.noMatches")}
          description={t("whatsapp.templates.list.noMatchesHint")}
        />
      ) : null}

      {loadState === "ready" && visibleItems.length > 0 ? (
        <div className="max-h-[max(20rem,calc(100dvh-26rem))] min-w-0 overflow-auto overscroll-contain rounded-xl border border-line bg-surface pb-2 shadow-sm">
          <table className="w-full border-separate border-spacing-0 text-sm">
            <thead>
              <tr>
                <th scope="col" className={headCellClass}>
                  {t("whatsapp.templates.list.columns.name")}
                </th>
                <th scope="col" className={`${headCellClass} ${centeredHeadClass} hidden md:table-cell`}>
                  {t("whatsapp.templates.list.columns.templateId")}
                </th>
                <th scope="col" className={`${headCellClass} ${centeredHeadClass}`}>
                  {t("whatsapp.templates.list.columns.status")}
                </th>
                <th scope="col" className={`${headCellClass} ${centeredHeadClass} hidden sm:table-cell`}>
                  {t("whatsapp.templates.list.columns.category")}
                </th>
                <th scope="col" className={`${headCellClass} ${centeredHeadClass} w-px`}>
                  <span className="sr-only sm:not-sr-only">
                    {t("whatsapp.templates.list.columns.actions")}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <AnimatePresence initial={false}>
              {visibleItems.map((template) => (
                <TemplateListRow
                  key={template.id}
                  organizationId={organizationId}
                  template={template}
                  canManage={showManageActions}
                  languageOptions={languageOptions}
                  onEdit={() => onEdit(template)}
                  onDuplicate={() => onDuplicate(template)}
                  onRequestDelete={() => onDelete(template)}
                  onCancelDelete={onCancelDelete}
                  onConfirmDelete={onConfirmDelete}
                  pendingDelete={pendingDeleteId === template.id}
                  deleting={deleting && pendingDeleteId === template.id}
                  deleteError={pendingDeleteId === template.id ? deleteError : null}
                  onAttachHeaderImage={onAttachHeaderImage}
                  previewOpen={previewTemplateId === template.id}
                  onTogglePreview={() =>
                    setPreviewTemplateId((current) =>
                      current === template.id ? null : template.id,
                    )
                  }
                />
              ))}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}
